import { Checkbox, Flex, useToast } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { Input, PhoneField } from "@equidefi/ui";
import { DateInput } from "@equidefi/ui/forms";
import { UsCitizenRadio } from "@/components/workflow/UsCitizenRadio";
import type { DTO } from "@equidefi/shared/types/dto";
import { dayjs } from "@equidefi/i18n";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z.object({
  first_name: z
    .string()
    .trim()
    .min(1, MESSAGE.REQUIRED("First name"))
    .min(2, "First name must be at least 2 characters")
    .regex(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY),

  middle_name: z
    .string()
    .nullable()
    .refine((value) => !value || ASCII_ONLY_REGEX.test(value.trim()), {
      message: MESSAGE.ASCII_ONLY,
    }),

  last_name: z
    .string()
    .trim()
    .min(1, MESSAGE.REQUIRED("Last name"))
    .min(2, "Last name must be at least 2 characters")
    .regex(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY),

  phone_number: z
    .string()
    .min(1, MESSAGE.REQUIRED("Mobile Phone"))
    .refine((value) => isValidPhoneNumber(value), {
      message: "Must be a valid phone number",
    }),

  citizenship: z.string().min(1, "Please select your citizenship"),

  birthdate: z
    .string()
    .min(1, MESSAGE.REQUIRED("Date of Birth"))
    .refine((value) => dayjs(value, "MM/DD/YYYY", true).isValid(), "Invalid birth date"),

  tax_id: z.string().trim().min(1, MESSAGE.REQUIRED("TIN")),
});

type PersonalDetailsFormValues = z.infer<typeof schema>;

type PersonalDetailsFormNormalized = {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  phone_number: string;
  citizenship: string;
  birthdate: string;
  tax_id: string;
};

type PersonalDetailsFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: PersonalDetailsFormNormalized) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile): PersonalDetailsFormValues => ({
  first_name: profile.first_name ?? "",
  middle_name: profile.middle_name ?? null,
  last_name: profile.last_name ?? "",
  phone_number: profile.phone_number ?? "",
  citizenship: profile.citizenship ?? "",
  birthdate:
    profile.birthdate && dayjs(profile.birthdate).isValid() ? dayjs(profile.birthdate).format("MM/DD/YYYY") : "",
  tax_id: profile.tax_id ?? "",
});

const normalizeValues = (values: PersonalDetailsFormValues): PersonalDetailsFormNormalized => ({
  first_name: values.first_name.trim(),
  middle_name: values.middle_name?.trim() || null,
  last_name: values.last_name.trim(),
  phone_number: values.phone_number,
  citizenship: values.citizenship,
  birthdate: dayjs(values.birthdate).format("YYYY-MM-DD"),
  tax_id: values.tax_id.trim(),
});

export function PersonalDetailsForm({ profile, onSave }: PersonalDetailsFormProps) {
  const toast = useToast();
  const [isTinVisible, setIsTinVisible] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<PersonalDetailsFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = useCallback(
    async (values: PersonalDetailsFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your personal details.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SavedProfileFormContainer
      profile={profile}
      title="Personal Details"
      onSave={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
      isDisabled={!isValid}>
      <Input
        label="First Name"
        isRequired
        error={errors.first_name?.message}
        isInvalid={!!errors.first_name}
        {...register("first_name")}
      />

      <Input
        label="Middle Name"
        error={errors.middle_name?.message}
        isInvalid={!!errors.middle_name}
        {...register("middle_name")}
      />

      <Input
        label="Last Name"
        isRequired
        error={errors.last_name?.message}
        isInvalid={!!errors.last_name}
        {...register("last_name")}
      />

      <Controller
        name="phone_number"
        control={control}
        render={({ field, fieldState }) => (
          <PhoneField
            name={field.name}
            label="Mobile Phone Number"
            isRequired
            value={field.value}
            onChange={(value) => field.onChange(value ?? "")}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
            isInvalid={!!fieldState.error}
          />
        )}
      />

      <Controller
        name="citizenship"
        control={control}
        render={({ field }) => <UsCitizenRadio value={field.value} onChange={field.onChange} />}
      />

      <DateInput
        label="Date of Birth"
        placeholder="MM/DD/YYYY"
        isRequired
        error={errors.birthdate?.message}
        isInvalid={!!errors.birthdate}
        {...register("birthdate")}
        onChange={(date: string) => {
          setValue("birthdate", date);
        }}
      />

      <Flex gap={3} align="flex-end">
        <Input
          label="TIN"
          isRequired
          type={isTinVisible ? "text" : "password"}
          error={errors.tax_id?.message}
          isInvalid={!!errors.tax_id}
          {...register("tax_id")}
        />

        <Checkbox
          mb={2}
          isChecked={isTinVisible}
          onChange={(e) => setIsTinVisible(e.target.checked)}
          size="sm"
          colorScheme="purple"
          whiteSpace="nowrap">
          Show TIN
        </Checkbox>
      </Flex>
    </SavedProfileFormContainer>
  );
}
